import { Component, computed, inject, OnInit, QueryList, signal, ViewChildren, ChangeDetectionStrategy } from '@angular/core';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';

import { FiltreButtonLineComponent } from './filtre-button-line/filtre-button-line.component';
import { FiltreManagerService } from '@lib/services/filtrer-investisseurs/filtre-manager.service';

import { FormControl, Validators } from '@angular/forms';
import { RechercheSauvegardeFragmentFragment } from '@lib/models/generated/graphql';
import { FiltreLine } from '@lib/services/filtrer-investisseurs/filtre-lines/filtre-line.model';
import { FiltreLineService } from '@lib/services/filtrer-investisseurs/filtre-lines/filtre-line.service';
import { FiltrerInvestisseursPanelService } from '@lib/services/filtrer-investisseurs/filtrer-investisseurs-panel.service';
import { FiltreCustomLineComponent } from './filtre-custom-line/filtre-custom-line.component';

@Component({
  selector: 'app-filtrer-investisseurs',
  imports: [SHARED_IMPORTS, FiltreCustomLineComponent, FiltreButtonLineComponent],
  templateUrl: './filtrer-investisseurs.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './filtrer-investisseurs.component.scss',
})
export class FiltrerInvestisseursComponent implements OnInit {
  @ViewChildren(FiltreCustomLineComponent) customchildren!: QueryList<FiltreCustomLineComponent>;
  @ViewChildren(FiltreButtonLineComponent) buttonchildren!: QueryList<FiltreButtonLineComponent>;
  protected filtreLineService = inject(FiltreLineService);
  protected filtreService = inject(FiltreManagerService);
  private readonly panelService = inject(FiltrerInvestisseursPanelService);
  private readonly maximumCustomLines = 10;

  editableRecherche = signal<RechercheSauvegardeFragmentFragment | undefined>(undefined);
  nomRechercheControl = new FormControl<string>(this.editableRecherche()?.label ?? '', {
    nonNullable: true,
    validators: [Validators.required],
  });

  localButtonLines = signal<FiltreLine[]>([]);
  localCustomLines = signal<FiltreLine[]>([]);

  readonly lockAddCustomLine = computed(() => this.localCustomLines().length >= this.maximumCustomLines);

  constructor() {
    this.localButtonLines.set(this.filtreLineService.buttonLines().map(l => l.withValue(l.value)));
    this.localCustomLines.set(this.filtreLineService.customLines().map(l => l.withValue(l.value)));
  }

  ngOnInit(): void {
    this.editableRecherche.set(this.filtreService.selectedRechercheSauvegarde());
    this.nomRechercheControl.setValue(this.editableRecherche()?.label ?? '');
  }

  addCustomLine() {
    const firstBuilder = this.filtreLineService['customBuilders'][0];
    if (!firstBuilder) return;
    if (this.localCustomLines().length >= this.maximumCustomLines) return;

    const newLine = new FiltreLine(
      firstBuilder.getThemeCode(),
      firstBuilder.getBuilderCode(),
      firstBuilder,
      this.localCustomLines().length
    );

    this.localCustomLines.update(lines => [...lines, newLine]);
  }

  onButtonLineChanged({ index, value }: { index: number; value: any }) {
    this.localButtonLines.update(lines => lines.map((l, i) => (i === index ? l.withValue(value) : l)));
  }

  onCustomLineChanged({ index, line }: { index: number; line: FiltreLine }) {
    this.localCustomLines.update(lines => lines.map((l, i) => (i === index ? line : l)));
  }
  onRemoveCustomLine(index: number) {
    this.localCustomLines.update(lines => lines.filter((_, i) => i !== index));
  }

  private runSearch() {
    this.syncToService();
    this.filtreService.updateFiltreInput();
  }

  onSearch(): void {
    this.runSearch();
    this.closeSlidePanel();
  }

  onClear(): void {
    this.resetLocalLines();
  }

  onSave(): void {
    const nom = this.nomRechercheControl.value;
    if (!this.editableRecherche() || !nom) return;
    this.syncToService();
    this.filtreService.updateFiltreInputAndRecherche(this.editableRecherche()?.id, nom);
    this.closeSlidePanel();
  }
  closeSlidePanel() {
    this.panelService.closeFilterPanel();
  }

  private syncToService() {
    this.filtreLineService.updateLines([...this.localButtonLines(), ...this.localCustomLines()]);
  }

  private resetLocalLines() {
    this.buttonchildren.forEach(b => b.clearSelection());
    this.localButtonLines.set(this.filtreLineService.buttonLines().map(l => l.withValue(null)));
    this.localCustomLines.set([]);
  }
}
